import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { authAPI, wishlistAPI } from '../../utils/api';
import { Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, loading, updateUser } = useAuth();
  const queryClient = useQueryClient();

  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [password, setPassword] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
    }
  }, [user]);

  const { data: wishlist = [], isLoading: wishlistLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => wishlistAPI.get().then((res) => res.data),
  });

  const updateProfile = useMutation({
    mutationFn: (data) => authAPI.updateProfile(data),
    onSuccess: () => {
      toast.success('Profili u përditësua');
      updateUser({ name: profile.name, phone: profile.phone });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Gabim gjatë përditësimit'),
  });

  const changePassword = useMutation({
    mutationFn: (data) => authAPI.changePassword(data),
    onSuccess: () => {
      toast.success('Fjalëkalimi u ndryshua me sukses');
      setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Gabim gjatë ndryshimit të fjalëkalimit'),
  });

  const removeFavorite = useMutation({
    mutationFn: (id) => wishlistAPI.remove(id),
    onSuccess: () => {
      toast.success('Produkt u hoq nga favorites');
      queryClient.invalidateQueries(['wishlist']);
    },
    onError: () => toast.error('Gabim gjatë heqjes'),
  });

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div className="page-header">
        <div>
          <p className="section-eyebrow">Profili</p>
          <h1 className="page-title">Llogaria ime</h1>
          <p className="page-sub">Menaxhoni informacionin e llogarisë dhe favorites tuaja.</p>
        </div>
        <Link to="/orders" className="btn btn-outline">Shiko Historikun e Porosive</Link>
      </div>

      <div className="profile-grid">
        <section className="profile-panel">
          <div className="panel-title">Të dhënat e profilit</div>
          <div className="card">
            <div className="form-group">
              <label className="form-label">Emri</label>
              <input className="form-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={profile.email} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Telefoni</label>
              <input className="form-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
            </div>
            <button className="btn btn-primary btn-full" onClick={() => updateProfile.mutate({ name: profile.name, phone: profile.phone })} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? 'Duke ruajtur...' : 'Ruaj ndryshimet'}
            </button>
          </div>

          <div className="card" style={{ marginTop: 24 }}>
            <div className="panel-title">Ndrysho fjalëkalimin</div>
            <div className="form-group">
              <label className="form-label">Fjalëkalimi aktual</label>
              <input className="form-input" type="password" value={password.currentPassword} onChange={(e) => setPassword({ ...password, currentPassword: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Fjalëkalimi i ri</label>
              <input className="form-input" type="password" value={password.newPassword} onChange={(e) => setPassword({ ...password, newPassword: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Konfirmo fjalëkalimin</label>
              <input className="form-input" type="password" value={password.confirmPassword} onChange={(e) => setPassword({ ...password, confirmPassword: e.target.value })} />
            </div>
            <button
              className="btn btn-secondary btn-full"
              onClick={() => {
                if (password.newPassword !== password.confirmPassword) {
                  toast.error('Fjalëkalimet nuk përputhen');
                  return;
                }
                if (!password.currentPassword || !password.newPassword) {
                  toast.error('Plotëso të gjitha fushat e fjalëkalimit');
                  return;
                }
                changePassword.mutate({ currentPassword: password.currentPassword, newPassword: password.newPassword });
              }}
              disabled={changePassword.isPending}
            >
              {changePassword.isPending ? 'Duke ndryshuar...' : 'Ndrysho fjalëkalimin'}
            </button>
          </div>
        </section>

        <section className="profile-panel">
          <div className="panel-title">Favorites</div>
          <div className="card">
            {wishlistLoading ? (
              <div className="loading-screen" style={{ minHeight: 220 }}><div className="spinner" /></div>
            ) : wishlist.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">💚</div>
                <h3>Nuk ka favorites ende</h3>
                <p>Shto produktet që të pëlqejnë dhe i gjej më lehtë.</p>
                <Link to="/shop" className="btn btn-primary">Shiko Dyqanin</Link>
              </div>
            ) : (
              <div className="favorites-list">
                {wishlist.map((item) => {
                  const images = (() => { try { return JSON.parse(item.images || '[]'); } catch { return [item.images]; } })();
                  return (
                    <div key={item.id} className="favorite-row">
                      <img src={images[0] || 'https://via.placeholder.com/80'} alt={item.name} />
                      <div>
                        <Link to={`/product/${item.slug}`} className="favorite-name">{item.name}</Link>
                        <div className="favorite-meta">${Number(item.price || 0).toFixed(2)}</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => removeFavorite.mutate(item.product_id)}>
                        <Trash2 size={14} /> Hiq
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      <style>{`
        .profile-grid { display: grid; grid-template-columns: 1.1fr .9fr; gap: 24px; }
        .profile-panel { display: flex; flex-direction: column; gap: 20px; }
        .panel-title { font-size: 1rem; font-weight: 700; color: var(--green-deep); text-transform: uppercase; letter-spacing: .08em; }
        .card { background: var(--white); border-radius: var(--radius-xl); padding: 24px; border: 1px solid var(--cream-dark); }
        .btn-secondary { background: var(--cream-dark); color: var(--green-deep); }
        .favorites-list { display: flex; flex-direction: column; gap: 16px; }
        .favorite-row { display: grid; grid-template-columns: 80px 1fr auto; gap: 16px; align-items: center; padding: 12px; border-bottom: 1px solid var(--cream-dark); }
        .favorite-row:last-child { border-bottom: none; }
        .favorite-row img { width: 80px; height: 80px; object-fit: cover; border-radius: var(--radius-md); background: var(--cream); }
        .favorite-name { font-weight: 600; color: var(--green-deep); }
        .favorite-meta { color: var(--text-light); font-size: .9rem; margin-top: 6px; }
        .empty-state { text-align: center; padding: 40px 20px; }
        .empty-state-icon { font-size: 2.5rem; margin-bottom: 16px; }
        @media(max-width: 980px) { .profile-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
